<template>
	<div class="Barchart" :class="classes">
		<!-- @slot for the barchart-->
		<slot />
		<table>
			<thead>
				<tr>
					<th v-for="(month, index) in months" :key="index" colspan="4" scope="col">
						<span aria-hidden="true">{{ monthNames[month] }}</span>
						<span>{{ longMonthNames[month] }}</span>
					</th>
				</tr>
			</thead>
			<tbody>
				<tr>
					<td
						v-for="(week, index) in chartData"
						:key="index"
						:class="{ needData: week.bar == -1 }"
						@mouseenter="mouseEnter(index)"
						@mouseleave="mouseLeave(index)"
					>
						<span :style="'height:' + week.bar + '%;'"></span>
						<span>
							{{ $t('generic.week') }} {{ week.weekNum }}
							<span v-if="week.bar == -1">{{ $t('barChart.insufficient') }}</span>
							<span v-else>
								{{ $t('barChart.frequency') }}
								{{ week.freq }}
							</span>
						</span>
						<span v-if="week.tooltip" class="Barchart-tooltip" :class="tooltipClass(index)">
							<template v-if="week.bar == -1">{{ $t('barChart.insufficient') }}</template>
							<template v-else>
								{{ $t('barChart.frequency') }}
								{{ week.freq }}%
							</template>
						</span>
					</td>
				</tr>
			</tbody>
			<caption>
				{{
					caption
				}}
			</caption>
		</table>
		<div class="Barchart-today" :style="todayStyle"></div>
	</div>
</template>

<script>
export default {
	name: 'CloBarchart',
	data() {
		return {
			months: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11],
		};
	},
	props: {
		/**
		 * Size can be sm or lg
		 */
		size: {
			type: String,
			default: '',
		},
		/**
		 * Region code
		 */
		regionCode: String,
		/**
		 * Code for the species
		 */
		speciesCode: String,
		/**
		 * Make chart asynchronous or not
		 */
		async: {
			type: Boolean,
			default: true,
		},
		/**
		 * Caption for the chart
		 */
		caption: {
			type: String,
			required: true,
		},
		/**
		 * Data for the chart
		 */
		chartData: {
			required: true,
			type: Array,
			default() {
				return [];
			},
		},
		/**
		 * Date used to position orange line (YYYY-MM-DD)
		 */
		date: {
			type: String,
			default: '',
		},
	},
	computed: {
		classes: function () {
			let classes = [];
			const prefix = 'Barchart--';
			if (this.size.length) {
				classes.push(prefix + this.size);
			}
			return classes.join(' ');
		},
		monthNames: function () {
			return this.months.map((monthIndex) => {
				let date = new Date('January 1, 2000 12:00:00');
				date.setMonth(monthIndex);
				const lang = navigator.language;
				return date.toLocaleDateString(lang, { month: 'narrow' });
			});
		},
		longMonthNames: function () {
			return this.months.map((monthIndex) => {
				let date = new Date('January 1, 2000 12:00:00');
				date.setMonth(monthIndex);
				const lang = navigator.language;
				return date.toLocaleDateString(lang, { month: 'long' });
			});
		},
		todayStyle: function () {
			// draw orange vertical line somewhere on over table
			const date = this.date.length ? new Date(this.date) : new Date();
			// console.log(`Positioning today at: ${date.toDateString()}`);
			const dayOfMonth = date.getDate();
			const month = date.getMonth();
			const numDaysInCurrentMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
			const percentOfYear = month / 12;
			const percentOfMonth = dayOfMonth / numDaysInCurrentMonth;
			const offset = 100 * percentOfYear + percentOfMonth * (100 / 12);
			const dir = document.querySelector('html').getAttribute('dir');
			if (dir && dir == 'rtl') {
				return 'right: ' + offset + '%';
			} else {
				return 'left: ' + offset + '%';
			}
		},
	},
	beforeMount() {
		this.chartData.forEach((item) => {
			item.tooltip = false;
		});
	},
	methods: {
		calculateBarHeight(percent) {
			return (percent / 100).toFixed(2) * this.maxHeight;
		},
		mouseEnter(index) {
			this.chartData[index].tooltip = true;
			this.$forceUpdate();
		},
		mouseLeave(index) {
			this.chartData[index].tooltip = false;
			this.$forceUpdate();
		},
		tooltipClass(index) {
			if (index > 24) {
				return 'Barchart-tooltip--right';
			}
		},
	},
};
</script>
<style lang="scss">
// Barchart styles

.Barchart {
	position: relative;
	width: 100%;

	caption {
		// @include visuallyHidden;
	}

	table {
		@include text(2);
		margin: 0;
		display: flex;
		flex-direction: column;
		flex-wrap: nowrap;
	}

	thead {
		display: flex;
		background-color: transparent;
		border: 0;
		tr {
			display: flex;
			width: 100%;
			th {
				display: flex; // needed for IE
				width: calc(1 / 12 * 100%);
				text-align: left;
				color: $color-text-muted;
				font-weight: normal;
				padding: 0;
				span:last-child {
					@include visuallyHidden;
				}
			}
		}
	}

	tbody {
		background-color: transparent;
		border: 0;
		display: flex;
		order: -1;
		@include stack-xxs;
		tr {
			display: flex;
			width: 100%;
			td {
				display: flex;
				align-items: center;
				padding: 0;
				width: calc(1 / 48 * 100%);
				height: $space-lg;
				background-color: $color-primary-1;
				font-size: 0;
				position: relative;
				&.needData {
					background: $color-neutral-0;
				}
				&:nth-child(4n + 1) {
					border-left: 1px solid $color-white;
					[dir='rtl'] & {
						border-left: 0;
						border-right: 1px solid $color-white;
					}
				}
				&:first-child {
					border-left: 0;
					[dir='rtl'] & {
						border-right: 0;
					}
				}
				span:first-child {
					display: block;
					width: 100%;
					background-color: $color-primary-4;
					border-right: 1px solid rgba(255, 255, 255, 0.2);
				}
				span:nth-child(2) {
					@include visuallyHidden;
				}
				span:nth-child(3) {
					display: block;
				}
			}
		}
	}
	caption {
		@include visuallyHidden;
		text-align: left;
		margin-top: $space-rel-sm;
	}
	.Barchart-today {
		position: absolute;
		top: 0;
		height: $space-lg;
		width: 1px;
		background-color: $color-alert-4;
	}
	.Barchart-tooltip {
		display: block;
		position: absolute;
		top: 100%;
		left: 0;
		right: auto;
		[dir='rtl'] & {
			left: auto;
			right: 0;
		}
		min-width: 2rem;
		text-align: center;
		@include inset-squish-sm;
		@include text(2);
		background-color: $color-white;
		color: $color-text;
		border: 1px solid $color-neutral-2;
		border-radius: $radius-sm;
		margin-top: $space-sm;
		@include dropShadow(2);
		word-break: normal;
		word-spacing: normal;
		word-wrap: normal;
		white-space: nowrap;
		z-index: 1000;
		&--right {
			left: auto;
			right: 0;
			[dir='rtl'] & {
				left: 0;
				right: auto;
			}
		}
	}
}

.Barchart--short, // --short is deprecated
.Barchart--sm {
	table {
		@include text(1);
	}
	tbody tr td {
		height: $space-md;
	}
	.Barchart-today {
		height: $space-md;
	}
}

.Barchart--lg {
	table {
		@include text(3);
	}
	tbody tr td {
		height: $space-xl;
	}
	.Barchart-today {
		height: $space-xl;
	}
}
</style>
